'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState, useEffect, Suspense } from 'react'

const Spotlight = ({spotlights, supplier, designer, orientation}) => {

    let url, title, content, spotlightType

    if (supplier && supplier.supplier_id > 0) {
        url = supplier.url
        title = supplier.supplier_name
        content = supplier.supplier_bio
        spotlightType = 'Brand'
    } else if (designer && designer.designer_id > 0) {
        url = designer.url
        title = designer.designer_name
        content = designer.long_bio
        spotlightType = 'Designer'
    }

    const [spotlightImages, setSpotlightImages] = useState(false);

    useEffect(() => {
        // randomize spotlight imagery if there are more than one
        const randomIndex = Math.floor(Math.random() * spotlights.length);
        const spotlight = spotlights[randomIndex];

        if (spotlight) {
            let i = {
                primaryImage: {
                    file_name: `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${spotlight.primary.file_name}`,
                    alt_text: spotlight.primary.alt_text,
                },
                secondaryImage: {
                    file_name: `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${spotlight.secondary.file_name}`,
                    alt_text: spotlight.secondary.alt_text,
                },
            }
            if (spotlight.brand_logo) {
                i.brand_logo = spotlight.brand_logo
            }
            setSpotlightImages(i)
        }
    }, [spotlights])

    const spotlightContent = (
        <div className={`flex flex-col flex-col-reverse md:flex-row align-items-start w-full max-w-[1800px] gap-0 ${orientation == 'ltor' ? 'md:flex-row-reverse' : ''}`}>
            <div className="w-full md:w-1/2 lg:w-1/3 flex flex-col justify-between bg-gray-3 site-padding py-5 md:py-4 lg:py-4 xl:py-8">
                <span className="uppercase text-3xs font-semibold text-ink-light-gray-bg mb-6 md:mb-3 lg:mb-6">
                    {spotlightType} Spotlight
                </span>
                <div className="mb-0">
                    {spotlightImages && spotlightImages.brand_logo ? (
                        <Image
                            className="max-w-[200px] md:max-w-[150px] lg:max-w-[200px] mb-4 md:mb-2 lg:mb-4"
                            src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${spotlightImages.brand_logo}`}
                            alt={`${title} logo`}
                            width={265}
                            height={26}
                            quality={90}
                            sizes="200px"
                        />
                    ) : (
                        <h3 className="text-primary font-medium mb-4 md:mb-2 lg:mb-4">{title}</h3>
                    )}
                    <p className="text-justify md:text-left text-base md:text-sm lg:text-sm xl:text-base text-ink-gray-bg mb-3 md:mb-2 lg:mb-2 xl:mb-3 leading-6 md:leading-5 lg:leading-6">
                        { content }
                    </p>
                    <span className="underline text-xs text-ink-gray-bg group-hover:text-primary transition-colors lg:hidden xl:inline">View {spotlightType}</span>
                </div>
            </div>
            <div className={`w-full lg:w-2/3 flex ${orientation == 'ltor' ? 'md:flex-row-reverse' : ''}`}>
                <div>
                    <Suspense>
                        {spotlightImages && spotlightImages.secondaryImage &&
                            <Image
                                className="hidden lg:block"
                                src={spotlightImages.secondaryImage.file_name}
                                alt={spotlightImages.secondaryImage.alt_text || `${title} spotlight image`}
                                width={300}
                                height={450}
                                quality={90}
                                sizes="(max-width: 1023px) 0px, 300px"
                            />
                        }
                    </Suspense>
                </div>
                <div className="sm:max-md:w-full">
                    <Suspense>
                        {spotlightImages && spotlightImages.primaryImage &&
                            <Image
                                className="sm:max-md:w-full"
                                src={spotlightImages.primaryImage.file_name}
                                alt={spotlightImages.primaryImage.alt_text || `${title} spotlight image`}
                                width={900}
                                height={450}
                                quality={90}
                                sizes="(max-width: 1023px) 100vw, 600px"
                            />
                        }
                    </Suspense>
                </div>
            </div>
        </div>
    )

    // Wrap entire spotlight in a single link for SEO and accessibility
    if (url) {
        return (
            <Link
                href={url}
                aria-label={`View ${title} ${spotlightType.toLowerCase()} collection`}
                className="group block transition-opacity hover:opacity-95"
            >
                {spotlightContent}
            </Link>
        )
    }

    return spotlightContent
}

export default Spotlight
